import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, BadgeCheck, ArrowUpRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { Reveal } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import SectionHead from "@/components/SectionHead";
import Seo from "@/components/Seo";
import { SpotlightLockup, useSpotlight } from "@/components/SpotlightSection";
import HEROES from "@/lib/pageHeroes.json";


export default function StancorGroup() {
  const { t, L } = useLang();
  const s = useSpotlight("show_page");

  if (s === undefined) return <div className="min-h-screen" data-testid="stancor-loading" />;
  if (!s) return <Navigate to="/joint-ventures" replace />;

  const paragraphs = (L(s.statement) || "").split("\n\n").filter(Boolean);

  return (
    <div data-testid="stancor-page">
      <Seo page="stancor" title={`${s.name} — ${t("spotlight_eyebrow")}`} description={L(s.tagline)} image={HEROES["stancor"]} />
      <PageHero eyebrow={L(s.eyebrow) || t("spotlight_eyebrow")} title={s.name} image={HEROES["stancor"]} />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 md:py-28">
        <Link to="/joint-ventures" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary" data-testid="stancor-back-link">
          <ArrowLeft className="h-4 w-4" /> {t("nav_ventures")}
        </Link>

        <div className="mt-10 grid gap-12 lg:grid-cols-[1fr_320px] lg:gap-16">
          <Reveal>
            <SpotlightLockup spotlight={s} size="lg" />
            {L(s.role) ? (
              <p className="mt-5 text-sm font-semibold uppercase tracking-[0.14em] text-primary" data-testid="stancor-role">{L(s.role)}</p>
            ) : null}
            {L(s.tagline) ? (
              <p className="mt-6 max-w-2xl font-head text-2xl leading-snug md:text-4xl">{L(s.tagline)}</p>
            ) : null}
            <div className="mt-10 space-y-6" data-testid="stancor-statement">
              {paragraphs.map((p, i) => (
                <p key={i} className="text-base leading-relaxed text-muted-foreground md:text-lg">{p}</p>
              ))}
            </div>
            {s.link ? (
              <a
                href={s.link}
                target="_blank"
                rel="noreferrer noopener"
                className="btn-outline mt-10 inline-flex"
                data-testid="stancor-external-link"
              >
                {t("spotlight_visit")} <ArrowUpRight className="ms-2 inline h-4 w-4" />
              </a>
            ) : null}
          </Reveal>

          <Reveal delay={0.08}>
            <aside className="border border-border bg-card/40 p-7" style={{ borderRadius: "var(--radius)" }}>
              {s.figures?.length ? (
                <>
                  <p className="eyebrow">{t("spotlight_scale")}</p>
                  <dl className="mt-6 space-y-6" data-testid="stancor-figures">
                    {s.figures.map((f, i) => (
                      <div key={i}>
                        <dt className="font-head text-3xl text-primary">{f.value}</dt>
                        <dd className="mt-1 text-xs uppercase tracking-[0.14em] text-muted-foreground">{L(f.label)}</dd>
                      </div>
                    ))}
                  </dl>
                </>
              ) : null}
              {s.approvals?.length ? (
                <>
                  <p className="eyebrow mt-10">{t("spotlight_approvals")}</p>
                  <ul className="mt-5 space-y-3" data-testid="stancor-approvals">
                    {s.approvals.map((a) => (
                      <li key={a} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {a}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </aside>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-border bg-card/20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 md:py-24">
          <SectionHead eyebrow={t("spotlight_reach_eyebrow")} title={t("spotlight_reach_title")} sub={t("spotlight_reach_sub")} testid="stancor-reach-head" />
          <Link to="/joint-ventures" className="btn-gold mt-10 inline-flex" data-testid="stancor-ventures-link">
            {t("spotlight_reach_cta")} <ArrowUpRight className="ms-2 inline h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
